import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import t from '@guestyci/localize/t.macro';
import {
  Button,
  Input,
  Text,
  Heading,
  Stack,
  Checkbox,
  Label,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  createSchema,
  email as emailValidator,
} from '@guestyci/arc';

import AppAdditionalContent from './AppAdditionalContent';

const emailFormSchema = createSchema({
  email: emailValidator(),
});
const EmailStep = ({
  email,
  onEmailChange,
  onContinue,
  onGoogleSignIn,
  error: externalError,
  isSubmitting,
  rememberMe,
  onRememberMeChange,
  showGoogleLogin = true,
  isWidgetRendered = true,
}) => {
  const form = useForm({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: email || '',
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (email !== form.getValues('email')) {
      form.setValue('email', email || '');
    }
  }, [email, form]);

  const handleSubmit = (data) => {
    onEmailChange(data.email);
    onContinue();
  };

  return (
    <Stack spacing={6} direction="column" className="gst-pt-6 lg:gst-pt-0">
      <Stack spacing={2} direction="column">
        <Heading variant="h1">{t('Welcome!')}</Heading>
        <Text type="secondary" size="base">
          {t('Please enter your details to sign in.')}
        </Text>

      </Stack>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
          <Stack spacing={6} direction="column">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Email')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      data-testid="signin-username"
                      type="email"
                      placeholder={t('e.g. name@company.com')}
                      autoFocus
                      autoComplete="email"
                      disabled={isSubmitting}
                      onChange={(e) => {
                        field.onChange(e);
                        onEmailChange(e.target.value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                  {externalError && !form.formState.errors.email && (
                    <Text type="error" size="sm">
                      {externalError}
                    </Text>
                  )}
                </FormItem>
              )}
            />

            <Stack spacing={2} direction="row" align="center">
              <Checkbox
                id="remember-me"
                checked={rememberMe}
                onCheckedChange={onRememberMeChange}
                disabled={isSubmitting}
              />
              <Label htmlFor="remember-me" className="gst-cursor-pointer">
                {t('Remember me')}
              </Label>
            </Stack>

            <Button
              type="submit"
              variant="default"
              className="gst-w-full"
              data-testid="signin-continue"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {t('Continue')}
            </Button>
          </Stack>
        </form>
      </Form>
      <AppAdditionalContent
        onGoogleSignIn={onGoogleSignIn}
        isSubmitting={isSubmitting}
        isWidgetRendered={isWidgetRendered}
        showGoogleLogin={showGoogleLogin}
      />
    </Stack>
  );
};

export default EmailStep;
